import React, { useEffect, useState, useCallback } from 'react';
import { Card, Button, Modal, Form, Badge, Spinner, Alert } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useNavigate } from 'react-router-dom';
import {
    fetchTenants, createTenant, updateTenant, deleteTenant, fetchAdminMeta,
    fetchProviders, fetchTenantProviders, linkTenantProvider, unlinkTenantProvider,
} from '../../services/adminApi';
import { useAuth } from '../../services/AuthContext';

function ConfirmModal({ show, onHide, onConfirm, title, message, confirmLabel, variant = 'warning' }) {
    const [busy, setBusy] = useState(false);
    const handleConfirm = async () => {
        setBusy(true);
        try { await onConfirm(); onHide(); }
        finally { setBusy(false); }
    };
    return (
        <Modal show={show} onHide={onHide} centered size="sm">
            <Modal.Header closeButton>
                <Modal.Title style={{ fontSize: 15 }}>{title}</Modal.Title>
            </Modal.Header>
            <Modal.Body style={{ fontSize: 13 }}>{message}</Modal.Body>
            <Modal.Footer>
                <Button variant="secondary" size="sm" onClick={onHide} disabled={busy}>Cancel</Button>
                <Button variant={variant} size="sm" onClick={handleConfirm} disabled={busy}>
                    {busy && <Spinner size="sm" animation="border" className="me-1" />}
                    {confirmLabel}
                </Button>
            </Modal.Footer>
        </Modal>
    );
}

// Presentation only (badge label/colour + dropdown description). The set of VALID types comes from
// the API (/admin/meta → waive_core.constants), so a new type renders without a code change here;
// anything without an entry falls back to its raw value.
const TYPE_META = {
    provider:       { label: 'Provider',       bg: '#dbeafe', color: '#1d4ed8', desc: 'Provider — fund management company (also a Waive client)' },
    corporate:      { label: 'Corporate',      bg: '#dcfce7', color: '#166534', desc: 'Corporate — employer whose staff have pensions/ISAs with a provider' },
    wealth_manager: { label: 'Wealth Manager', bg: '#fef9c3', color: '#854d0e', desc: 'Wealth Manager — distributes funds from multiple providers' },
    waive:          { label: 'Waive',          bg: '#f3e8ff', color: '#6b21a8', desc: 'Waive — Waive Ltd internal' },
    d2c:            { label: 'Direct (D2C)',    bg: '#fae8ff', color: '#a21caf', desc: 'Direct-to-consumer — individual self-service member' },
};

const EMPTY_FORM = {
    code: '', name: '', tenant_type: 'corporate', email_domains: '', crm_api_url: '', subdomain: '',
};

function TypeBadge({ type }) {
    const meta = TYPE_META[type] || { label: type, bg: '#f1f5f9', color: '#475569' };
    return (
        <span className="badge rounded-pill" style={{ background: meta.bg, color: meta.color, fontWeight: 600, fontSize: 11 }}>
            {meta.label}
        </span>
    );
}

function TenantModal({ show, onHide, onSave, initial, tenantTypes }) {
    const { can } = useAuth();
    const [form, setForm] = useState(EMPTY_FORM);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);
    const editing = !!initial;

    useEffect(() => {
        if (show) {
            setForm(initial
                ? { ...initial, email_domains: (initial.email_domains || []).join(', ') }
                : EMPTY_FORM
            );
            setError(null);
        }
    }, [show, initial]);

    const set = (field, value) => setForm(f => ({ ...f, [field]: value }));

    const handleSave = async () => {
        if (!form.code.trim() || !form.name.trim()) {
            setError('Code and name are required.');
            return;
        }
        setSaving(true);
        setError(null);
        try {
            const domains = form.email_domains
                ? form.email_domains.split(',').map(d => d.trim()).filter(Boolean)
                : [];
            const payload = {
                name: form.name.trim(),
                tenant_type: form.tenant_type,
                email_domains: domains,
                crm_api_url: form.crm_api_url?.trim() || null,
                subdomain: form.subdomain?.trim().toLowerCase() || null,
            };
            if (!editing) payload.code = form.code.trim().toUpperCase();
            await onSave(payload);
            onHide();
        } catch (e) {
            setError(e.response?.data?.detail || 'Save failed.');
        } finally {
            setSaving(false);
        }
    };

    return (
        <Modal show={show} onHide={onHide} centered>
            <Modal.Header closeButton>
                <Modal.Title style={{ fontSize: 16 }}>{editing ? 'Edit Tenant' : 'New Tenant'}</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                {error && <Alert variant="danger" className="py-2" style={{ fontSize: 13 }}>{error}</Alert>}
                <Form>
                    {!editing && (
                        <Form.Group className="mb-3">
                            <Form.Label style={{ fontSize: 13, fontWeight: 600 }}>Code</Form.Label>
                            <Form.Control size="sm" data-testid="tenant-code" value={form.code}
                                onChange={e => set('code', e.target.value.toUpperCase())}
                                placeholder="e.g. ACME" maxLength={64} />
                            <Form.Text className="text-muted">Short uppercase identifier. Cannot be changed later.</Form.Text>
                        </Form.Group>
                    )}
                    <Form.Group className="mb-3">
                        <Form.Label style={{ fontSize: 13, fontWeight: 600 }}>Name</Form.Label>
                        <Form.Control size="sm" data-testid="tenant-name" value={form.name} onChange={e => set('name', e.target.value)} placeholder="Full organisation name" />
                    </Form.Group>
                    <Form.Group className="mb-3">
                        <Form.Label style={{ fontSize: 13, fontWeight: 600 }}>Type</Form.Label>
                        <Form.Select size="sm" value={form.tenant_type} onChange={e => set('tenant_type', e.target.value)}>
                            {(tenantTypes || []).map(t => (
                                <option key={t} value={t}>{TYPE_META[t]?.desc || TYPE_META[t]?.label || t}</option>
                            ))}
                        </Form.Select>
                    </Form.Group>
                    <Form.Group className="mb-3">
                        <Form.Label style={{ fontSize: 13, fontWeight: 600 }}>Email Domains</Form.Label>
                        <Form.Control size="sm" value={form.email_domains}
                            onChange={e => set('email_domains', e.target.value)}
                            placeholder="lgim.com, legalandgeneral.com" />
                        <Form.Text className="text-muted">Comma-separated. Used for pre-auth tenant routing.</Form.Text>
                    </Form.Group>
                    <Form.Group className="mb-3">
                        <Form.Label style={{ fontSize: 13, fontWeight: 600 }}>CRM API URL</Form.Label>
                        <Form.Control size="sm" value={form.crm_api_url || ''}
                            onChange={e => set('crm_api_url', e.target.value)}
                            placeholder="https://crm.example.com/api (optional)" />
                        <Form.Text className="text-muted">Leave blank if this tenant has no CRM integration.</Form.Text>
                    </Form.Group>
                    <Form.Group className="mb-3">
                        <Form.Label style={{ fontSize: 13, fontWeight: 600 }}>Subdomain <span className="text-muted fw-normal">(optional)</span></Form.Label>
                        <Form.Control size="sm" value={form.subdomain || ''}
                            onChange={e => set('subdomain', e.target.value)}
                            placeholder="acme.mywaive.com" />
                        <Form.Text className="text-muted">White-label Advice-Portal host. Routes the member to this tenant's IdP. Leave blank if none.</Form.Text>
                    </Form.Group>
                    <Form.Text className="text-muted d-block">
                        Identity provider (SSO / CIAM) is configured on the tenant's <strong>Identity</strong> tab.
                    </Form.Text>
                </Form>
            </Modal.Body>
            <Modal.Footer>
                <Button variant="secondary" size="sm" onClick={onHide} disabled={saving}>Cancel</Button>
                <Button variant="primary" size="sm" onClick={handleSave} disabled={saving || !can.onboardTenant()}
                        title={can.onboardTenant() ? undefined : "Requires the waive-onboarding role"}>
                    {saving ? <Spinner size="sm" animation="border" className="me-1" /> : null}
                    {editing ? 'Save Changes' : 'Create Tenant'}
                </Button>
            </Modal.Footer>
        </Modal>
    );
}

// ADR-0028: manage which providers a tenant's members authenticate through. A member's token
// issuer must resolve to one of these providers (the tenant↔provider pairing), else the API 403s.
function ProvidersModal({ show, onHide, tenant }) {
    const [allProviders, setAllProviders] = useState([]);
    const [linked, setLinked] = useState(new Set());   // provider codes
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [busyCode, setBusyCode] = useState(null);

    const load = useCallback(() => {
        if (!tenant) return;
        setLoading(true);
        Promise.all([fetchProviders(), fetchTenantProviders(tenant.code)])
            .then(([all, linkedRes]) => {
                setAllProviders(all.providers || []);
                setLinked(new Set((linkedRes.providers || []).map(p => p.code)));
            })
            .catch(() => setError('Failed to load providers.'))
            .finally(() => setLoading(false));
    }, [tenant]);

    useEffect(() => { if (show) { setError(null); load(); } }, [show, load]);

    const toggle = async (code, isLinked) => {
        setBusyCode(code);
        setError(null);
        try {
            if (isLinked) await unlinkTenantProvider(tenant.code, code);
            else await linkTenantProvider(tenant.code, code);
            setLinked(prev => {
                const next = new Set(prev);
                if (isLinked) next.delete(code); else next.add(code);
                return next;
            });
        } catch (e) {
            setError(e.response?.data?.detail || 'Update failed.');
        } finally {
            setBusyCode(null);
        }
    };

    return (
        <Modal show={show} onHide={onHide} centered>
            <Modal.Header closeButton>
                <Modal.Title style={{ fontSize: 16 }}>Providers — {tenant?.name}</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <p className="text-muted" style={{ fontSize: 13 }}>
                    The providers this tenant&apos;s members authenticate through. A member&apos;s token
                    issuer must resolve to one of these, or the request is rejected (ADR-0028).
                </p>
                {error && <Alert variant="danger" className="py-2" style={{ fontSize: 13 }}>{error}</Alert>}
                {loading ? (
                    <div className="text-center py-4"><Spinner animation="border" variant="primary" /></div>
                ) : allProviders.length === 0 ? (
                    <p className="text-muted" style={{ fontSize: 13 }}>No providers defined yet.</p>
                ) : (
                    <div className="d-flex flex-column gap-2">
                        {allProviders.map(p => {
                            const isLinked = linked.has(p.code);
                            return (
                                <div key={p.id} className="d-flex align-items-center justify-content-between border rounded px-3 py-2">
                                    <div>
                                        <span className="fw-semibold" style={{ fontFamily: 'monospace', fontSize: 13 }}>{p.code}</span>
                                        <span className="text-muted ms-2" style={{ fontSize: 13 }}>{p.name}</span>
                                        {!p.issuer && (
                                            <Badge bg="light" text="dark" className="ms-2 border" style={{ fontSize: 10 }}>no issuer</Badge>
                                        )}
                                    </div>
                                    <Form.Check type="switch" checked={isLinked}
                                        disabled={busyCode === p.code}
                                        onChange={() => toggle(p.code, isLinked)} />
                                </div>
                            );
                        })}
                    </div>
                )}
            </Modal.Body>
            <Modal.Footer>
                <Button variant="secondary" size="sm" onClick={onHide}>Done</Button>
            </Modal.Footer>
        </Modal>
    );
}

// Permanent, irreversible deletion (ADR-0032/0050): removes the hub tenant row AND tears down the
// white-label subdomain (AFD custom domain + DNS). Type-the-code guard so it can't be a mis-click;
// gated by waive-onboarding. Partial-teardown warnings from the API are surfaced (the delete still
// completed — an orphaned Azure resource is recoverable, a stuck tenant row is not).
function DeleteTenantModal({ show, onHide, tenant, onDeleted }) {
    const { can } = useAuth();
    const [typed, setTyped] = useState('');
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState(null);
    const [warnings, setWarnings] = useState(null);

    useEffect(() => {
        if (show) { setTyped(''); setError(null); setWarnings(null); }
    }, [show]);

    if (!tenant) return null;
    const confirmed = typed.trim().toUpperCase() === tenant.code.toUpperCase();
    const allowed = can.onboardTenant();

    const handleDelete = async () => {
        setBusy(true);
        setError(null);
        try {
            const res = await deleteTenant(tenant.code);
            onDeleted();   // refresh the list so the row disappears
            if (res.warnings && res.warnings.length) setWarnings(res.warnings);
            else onHide();
        } catch (e) {
            setError(e.response?.data?.detail || 'Delete failed.');
        } finally {
            setBusy(false);
        }
    };

    return (
        <Modal show={show} onHide={onHide} centered>
            <Modal.Header closeButton>
                <Modal.Title style={{ fontSize: 16 }} className="text-danger">
                    <FontAwesomeIcon icon="exclamation-triangle" className="me-2" />Delete tenant
                </Modal.Title>
            </Modal.Header>
            <Modal.Body style={{ fontSize: 13 }}>
                {warnings ? (
                    <Alert variant="warning" className="py-2 mb-0" data-testid="delete-warnings">
                        Tenant <strong>{tenant.code}</strong> was deleted, but some teardown steps need a manual check:
                        <ul className="mb-0 mt-1">{warnings.map((w, i) => <li key={i}>{w}</li>)}</ul>
                    </Alert>
                ) : (
                    <>
                        <p>
                            This <strong>permanently deletes</strong> tenant <strong>{tenant.name}</strong>{' '}
                            (<span style={{ fontFamily: 'monospace' }}>{tenant.code}</span>) and{' '}
                            <strong>removes its white-label subdomain</strong> (custom domain + DNS).
                            {tenant.subdomain && <> The host <code>{tenant.subdomain}</code> will stop resolving.</>}
                        </p>
                        <p className="text-muted">Members of this tenant lose access. This cannot be undone.</p>
                        {error && <Alert variant="danger" className="py-2">{error}</Alert>}
                        <Form.Group>
                            <Form.Label style={{ fontWeight: 600 }}>
                                Type <span style={{ fontFamily: 'monospace' }}>{tenant.code}</span> to confirm
                            </Form.Label>
                            <Form.Control size="sm" data-testid="delete-confirm-code" value={typed}
                                autoComplete="off" onChange={e => setTyped(e.target.value)} />
                        </Form.Group>
                    </>
                )}
            </Modal.Body>
            <Modal.Footer>
                {warnings ? (
                    <Button variant="secondary" size="sm" onClick={onHide}>Close</Button>
                ) : (
                    <>
                        <Button variant="secondary" size="sm" onClick={onHide} disabled={busy}>Cancel</Button>
                        <Button variant="danger" size="sm" data-testid="delete-tenant-confirm" onClick={handleDelete}
                            disabled={busy || !confirmed || !allowed}
                            title={allowed ? undefined : 'Requires the waive-onboarding role'}>
                            {busy && <Spinner size="sm" animation="border" className="me-1" />}
                            Delete tenant
                        </Button>
                    </>
                )}
            </Modal.Footer>
        </Modal>
    );
}

function Tenants() {
    const [tenants, setTenants] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [modalOpen, setModalOpen] = useState(false);
    const [editing, setEditing] = useState(null);
    const [confirm, setConfirm] = useState(null);
    const [deleting, setDeleting] = useState(null);  // tenant being permanently deleted
    const [linking, setLinking] = useState(null);   // tenant whose providers are being managed
    const [filterType, setFilterType] = useState('');
    const [tenantTypes, setTenantTypes] = useState([]);   // valid types from /admin/meta
    const navigate = useNavigate();

    const load = useCallback(() => {
        setLoading(true);
        fetchTenants()
            .then(data => setTenants(data.tenants || []))
            .catch(() => setError('Failed to load tenants.'))
            .finally(() => setLoading(false));
    }, []);

    useEffect(() => { load(); }, [load]);
    useEffect(() => {
        fetchAdminMeta().then(m => {
            setTenantTypes(m.tenant_types || []);
        }).catch(() => {});
    }, []);

    const openCreate = () => { setEditing(null); setModalOpen(true); };
    const openEdit = (tenant) => { setEditing(tenant); setModalOpen(true); };

    const handleSave = async (payload) => {
        if (editing) {
            await updateTenant(editing.id, payload);
        } else {
            await createTenant(payload);
        }
        load();
    };

    const handleToggleActive = async () => {
        await updateTenant(confirm.tenant.id, { active: confirm.nextActive });
        setConfirm(null);
        load();
    };

    const displayed = filterType ? tenants.filter(t => t.tenant_type === filterType) : tenants;

    return (
        <div className="page-content">
            <div className="page-header">
                <h1 className="page-title">Tenants</h1>
                <Button variant="primary" size="sm" onClick={openCreate}>
                    <FontAwesomeIcon icon="plus" className="me-2" />New Tenant
                </Button>
            </div>

            {error && <Alert variant="danger" className="mb-3">{error}</Alert>}

            <Card className="border-0 shadow-sm">
                <Card.Header className="bg-white border-bottom d-flex align-items-center justify-content-between py-2">
                    <span style={{ fontSize: 13, fontWeight: 600 }}>
                        {displayed.length} tenant{displayed.length !== 1 ? 's' : ''}
                    </span>
                    <Form.Select size="sm" data-testid="tenant-type-filter" value={filterType} onChange={e => setFilterType(e.target.value)}
                        style={{ width: 180 }}>
                        <option value="">All types</option>
                        {Object.entries(TYPE_META).map(([k, v]) => (
                            <option key={k} value={k}>{v.label}</option>
                        ))}
                    </Form.Select>
                </Card.Header>
                <Card.Body className="p-0">
                    {loading ? (
                        <div className="text-center py-5"><Spinner animation="border" variant="primary" /></div>
                    ) : (
                        <table className="table table-hover mb-0" style={{ fontSize: 13 }}>
                            <thead className="table-light">
                                <tr>
                                    <th className="px-4 py-3 fw-medium" style={{ color: '#64748b' }}>Code</th>
                                    <th className="px-4 py-3 fw-medium" style={{ color: '#64748b' }}>Name</th>
                                    <th className="px-4 py-3 fw-medium" style={{ color: '#64748b' }}>Type</th>
                                    <th className="px-4 py-3 fw-medium" style={{ color: '#64748b' }}>Email Domains</th>
                                    <th className="px-4 py-3 fw-medium" style={{ color: '#64748b' }}>Status</th>
                                    <th className="px-4 py-3 fw-medium" style={{ color: '#64748b' }}>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                            {displayed.map(t => (
                                <tr key={t.id}>
                                    <td className="px-4 py-3 fw-semibold" style={{ fontFamily: 'monospace' }}>{t.code}</td>
                                    <td className="px-4 py-3">{t.name}</td>
                                    <td className="px-4 py-3"><TypeBadge type={t.tenant_type} /></td>
                                    <td className="px-4 py-3" style={{ color: '#64748b' }}>
                                        {(t.email_domains || []).join(', ') || '—'}
                                    </td>
                                    <td className="px-4 py-3">
                                        <Badge bg={t.active ? 'success' : 'secondary'} style={{ fontSize: 11 }}>
                                            {t.active ? 'Active' : 'Inactive'}
                                        </Badge>
                                    </td>
                                    <td className="px-4 py-3">
                                        <Button variant="outline-primary" size="sm" className="me-2"
                                            onClick={() => navigate(`/tenants/${t.code}`)}
                                            title="Manage SSO onboarding, configuration, scoring & questions">
                                            Manage <FontAwesomeIcon icon="chevron-right" className="ms-1" />
                                        </Button>
                                        <Button variant="outline-secondary" size="sm" className="me-2"
                                            onClick={() => openEdit(t)} title="Edit">
                                            <FontAwesomeIcon icon="pen" />
                                        </Button>
                                        <Button variant="outline-secondary" size="sm" className="me-2"
                                            onClick={() => setLinking(t)} title="Providers">
                                            <FontAwesomeIcon icon="sitemap" />
                                        </Button>
                                        <Button variant={t.active ? 'outline-warning' : 'outline-success'} size="sm" className="me-2"
                                            onClick={() => setConfirm({ tenant: t, nextActive: !t.active })}
                                            title={t.active ? 'Deactivate' : 'Activate'}>
                                            <FontAwesomeIcon icon={t.active ? 'ban' : 'check'} />
                                        </Button>
                                        <Button variant="outline-danger" size="sm" data-testid={`delete-tenant-${t.code}`}
                                            onClick={() => setDeleting(t)}
                                            title="Permanently delete this tenant and remove its subdomain">
                                            <FontAwesomeIcon icon="trash" />
                                        </Button>
                                    </td>
                                </tr>
                            ))}
                            {displayed.length === 0 && (
                                <tr><td colSpan={6} className="text-center text-muted py-5">No tenants found</td></tr>
                            )}
                            </tbody>
                        </table>
                    )}
                </Card.Body>
            </Card>

            <TenantModal
                show={modalOpen}
                onHide={() => setModalOpen(false)}
                onSave={handleSave}
                initial={editing}
                tenantTypes={tenantTypes}
            />

            <ProvidersModal
                show={!!linking}
                onHide={() => setLinking(null)}
                tenant={linking}
            />

            <DeleteTenantModal
                show={!!deleting}
                onHide={() => setDeleting(null)}
                tenant={deleting}
                onDeleted={load}
            />

            {confirm && (
                <ConfirmModal
                    show
                    onHide={() => setConfirm(null)}
                    onConfirm={handleToggleActive}
                    title={confirm.nextActive ? 'Activate tenant' : 'Deactivate tenant'}
                    message={confirm.nextActive
                        ? `Activate "${confirm.tenant.name}"?`
                        : `Deactivate "${confirm.tenant.name}"? Users in this tenant will not be able to log in.`}
                    confirmLabel={confirm.nextActive ? 'Activate' : 'Deactivate'}
                    variant={confirm.nextActive ? 'success' : 'warning'}
                />
            )}
        </div>
    );
}

export default Tenants;
